// src/Dashboard.js
import React, { useState, useEffect, useRef } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from './config/api';

import DashboardNavbar from './Components/Navbar/DashboardNavbar';
import Header from './Components/Header/BlueHeader';
// To revert the new top-row layout, swap this import back to './Components/Pills/Pills'
// and pass `<Pills stats={stats} hvacType={hvacType} />` below.
import Pills from './Components/Pills/PillsV2';
import SearchBar from './Components/SearchBar/SearchBar';
import FancoilTable from './Components/FancoilTable/FancoilTable';
import ProjectSummary from './Components/Projects/ProjectSummary';
import LocalProjectPreviewPicker from './Components/Projects/LocalProjectPreviewPicker';
import { isProjectMaintenanceCycle, getProjectStatus, getProjectNotes } from './projectView';
import Footer from './Components/Footer/Footer';
import IssueMultiSelect from './Components/IssueMultiSelect/IssueMultiSelect';
import PieChart from './Components/PieChart/PieChart';
import BuildingInsights from './Components/BuildingInsights/BuildingInsights';
import Joyride from 'react-joyride';
import RiserHealthChart from './Components/RiserHealthChart/RiserHealthChart';
import Spinner from 'react-bootstrap/Spinner';
import { Row, Col, Card, Button } from 'react-bootstrap';
import NotifyResidentModal from './Components/NotifyResidentModal/NotifyResidentModal';
import ShareDeficienciesGuide from './Components/ShareDeficienciesGuide/ShareDeficienciesGuide';
import Alert from 'react-bootstrap/Alert';
import StatusMultiSelect from './Components/StatusMultiSelect/StatusMultiSelect';
import Sidebar from './Components/Sidebar/Sidebar';
import { FaSnowflake, FaEnvelopeOpenText } from 'react-icons/fa';

const getCycleTypeValue = (cycle = {}) => {
  const rawType =
    cycle.Type ||
    cycle.type ||
    cycle.inspectionType ||
    cycle.inspection_type ||
    cycle.contractType ||
    cycle.contract_type ||
    cycle.serviceType ||
    cycle.service_type ||
    '';
  return typeof rawType === 'string' ? rawType.toLowerCase() : '';
};

const isDryerCycle = (cycle) => getCycleTypeValue(cycle).includes('dryer');
const isKitchenStackCycle = (cycle) => getCycleTypeValue(cycle).includes('kitchen stack');
const isValveAuditCycle = (cycle) =>
  getCycleTypeValue(cycle).includes('valve_audit') ||
  cycle.dataFormatVersion === 'valve_audit' ||
  (cycle.maintenanceID || '').includes('-valve');
// Project cycles (e.g. "Actuator Project") should NEVER appear on the in-suite
// dashboard — they have their own tab. Detect them via Type/maintenanceType
// containing "project" or via the harbour_actuator dataFormatVersion.
const isProjectCycle = (cycle = {}) =>
  getCycleTypeValue(cycle).includes('project') ||
  cycle.dataFormatVersion === 'harbour_actuator';

// True only for cycles that belong on the in-suite (HVAC / fancoil / heatpump) tab.
const isInsuiteCycle = (cycle) =>
  !isDryerCycle(cycle) &&
  !isKitchenStackCycle(cycle) &&
  !isValveAuditCycle(cycle) &&
  !isProjectCycle(cycle);

// Map a cycle to the route of the tab it belongs on (used for the post-login
// smart redirect when the in-suite tab has no cycles to show).
const routeForCycle = (cycle) => {
  if (isDryerCycle(cycle)) return '/dryer-vents';
  if (isKitchenStackCycle(cycle)) return '/stack-cleaning';
  if (isProjectCycle(cycle)) return '/projects';
  if (isValveAuditCycle(cycle)) return '/valve-audit';
  return null;
};

// Helper: determine if a file is a video
function isVideoUrl(url) {
  const videoExts = [".mp4", ".mov", ".webm", ".ogg", ".m4v"];
  return videoExts.some((ext) => url.toLowerCase().endsWith(ext));
}

function Dashboard() {
  const navigate = useNavigate();
  
  // Update sidebar initial state to be collapsed
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [activeKey, setActiveKey] = useState('hvac');

  const [buildingName, setBuildingName] = useState("");
  const [buildingManagerName, setBuildingManagerName] = useState();
  const [buildingPhotoUrl, setBuildingPhotoUrl] = useState("");
  const [hvacType, setHvacType] = useState("fancoil"); // NEW

  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [showShareGuide, setShowShareGuide] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [residentEmail, setResidentEmail] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  


  // State for building cycles and which one is selected
  const [maintenanceCycles, setMaintenanceCycles] = useState([]); 
  const [selectedCycle, setSelectedCycle] = useState("");         
  const [selectedMaintenance, setSelectedMaintenance] = useState(null);
  const projectMode = isProjectMaintenanceCycle(selectedMaintenance || { cycle: selectedCycle });
  const contractLabel = projectMode ? 'Project' : 'In-Suite Preventive Maintenance';
  const [projectStatusFilter, setProjectStatusFilter] = useState('All Statuses');

  // State for stats, table data
  const [stats, setStats] = useState({ totalUnits: 0, noIssues: 0, foundIssues: 0 });
  const [unitsData, setUnitsData] = useState([]);
  const [issueCounts, setIssueCounts] = useState({});
  const [dataFormat, setDataFormat] = useState("old"); // Track data format: "old" or "new"

  // Contract check
  const [hasHvacContract, setHasHvacContract] = useState(null);
  const [hasProjectContract, setHasProjectContract] = useState(false);
  const [hasValveAuditContract, setHasValveAuditContract] = useState(false);

  // Joyride / Tour
  const hasSeenTour = localStorage.getItem('hasSeenTour') === 'true';
  const [runTour, setRunTour] = useState(!hasSeenTour);

  // Loading
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [isCycleLoading, setIsCycleLoading] = useState(false);
  const [cycleLoadError, setCycleLoadError] = useState('');
  const isInitialLoadRef = useRef(true);

  // Table logic
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortColumn, setSortColumn] = useState('unit');
  const [sortDirection, setSortDirection] = useState('asc');
  const [fancoilFilter] = useState('All'); // (unused?)
  const [searchTerm, setSearchTerm] = useState('');
  const [issueFilters, setIssueFilters] = useState([]);
  // Multi-status filter — defaults to just "Needs Service" so managers see
  // outstanding issues first; Missed is one click away in the dropdown.
  const [statusFilter, setStatusFilter] = useState(['Needs Service']);

  // Modal slideshow
  const [showModal, setShowModal] = useState(false);
  const [carouselPhotos, setCarouselPhotos] = useState([]);
  const [carouselIndex, setCarouselIndex] = useState(0);

  const [showInviteSuccess, setShowInviteSuccess] = useState(false);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCycle, searchTerm, statusFilter, issueFilters, projectStatusFilter]);

  // Column map for searching, etc.
  const columnMap = {
    "Thermostat": "thermostat",
    "Blower Fan": "blower_fan",
    "Actuator": "actuator",
    "3 Way Valves": "n3way_valves",
    "Drain Pan": "drip_pan__sensor",
    "Valves": "valves",
    "Hoses": "hoses",
    "Mould": "mould",
    "Potential Mould": "mould",
    "Coils": "coils",
    "Compressor": "compressor",
    "Glycol Leakage": "glycol_leakage",
    "Leak Sensor": "leak_sensor",
    "Insulation": "insulation",
    "Other": "other"
  };

  // Helper function to normalize issue keys and combine counts for drain pan variations
  const normalizeIssueCounts = (counts) => {
    const normalized = {};
    const drainPanKeys = ['drip_pan__sensor', 'drip_pan_sensor', 'drip_pan', 'drain_pan', 'drain_pan_sensor'];
    let drainPanTotal = 0;
    
    Object.entries(counts).forEach(([key, count]) => {
      if (drainPanKeys.includes(key)) {
        // Combine all drain pan variations into one
        drainPanTotal += count;
      } else {
        normalized[key] = count;
      }
    });
    
    // Add combined drain pan count if any drain pan issues were found
    if (drainPanTotal > 0) {
      normalized['drip_pan__sensor'] = drainPanTotal; // Use the standard key
    }
    
    return normalized;
  };

  // Helper function to normalize issue names (standardize display names)
  // This matches the logic in unit-report.html to ensure consistency
  const normalizeIssueNames = (issues) => {
    if (!Array.isArray(issues)) return [];
    
    // Map of all possible issue name variations to standardized display names
    const issueNameMap = {
      "mould": "Potential Mould",
      "drip_pan": "Drain Pan",
      "drip_pan_sensor": "Drain Pan",
      "drip_pan__sensor": "Drain Pan",
      "drain_pan": "Drain Pan",
      "drain_pan_sensor": "Drain Pan",
      "thermostat": "Thermostat",
      "actuator": "Actuator",
      "3_way_valve": "3-Way Valve",
      "3 way valve": "3-Way Valve",
      "3-Way Valve": "3-Way Valve",
      "3 Way Valve": "3-Way Valve",
      "3-Way Valves": "3-Way Valve",
      "3 Way Valves": "3-Way Valve",
      "field_3_way_valves": "3-Way Valve",
      "n3way_valves": "3-Way Valve",
      "valves": "Valves",
      "hoses": "Hoses",
      "coils": "Coils",
      "compressor": "Compressor",
      "blower_fan": "Blower Fan",
      "bathroom_fan": "Bathroom Fan",
      "booster_fan": "Booster Fan",
      "exhaust_hose": "Exhaust Hose",
      "leak_sensor": "Leak Sensor",
      "dryer": "Dryer",
      "insulation": "Insulation",
      "other": "Other",
      "custom": "Other",
      "custom_issue": "Other",
      "custom issue": "Other",
      "any_other_custom_issues": "Other",
      "anyothercustomissues": "Other",
      "any other custom issues": "Other",
      "anyOtherCustomIssues": "Other"
    };
    
    // Normalize each issue name
    const normalizedSet = new Set();
    issues.forEach(issue => {
      if (!issue) return;
      const issueLower = issue.toLowerCase().trim();
      // Try exact match first
      if (issueNameMap[issueLower]) {
        normalizedSet.add(issueNameMap[issueLower]);
      } else if (issueNameMap[issue]) {
        // Try case-sensitive match
        normalizedSet.add(issueNameMap[issue]);
      } else {
        // Try to find a match by checking if any key contains this issue
        let found = false;
        for (const [key, displayName] of Object.entries(issueNameMap)) {
          if (key.toLowerCase() === issueLower || issueLower.includes(key.toLowerCase()) || key.toLowerCase().includes(issueLower)) {
            normalizedSet.add(displayName);
            found = true;
            break;
          }
        }
        // If no match found, use the original (capitalized)
        if (!found) {
          normalizedSet.add(issue.split(" ").map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" "));
        }
      }
    });
    
    return Array.from(normalizedSet).sort();
  };

  // Filter values stay on the legacy labels (e.g. "Mould") while row.issues
  // hold normalized display names (e.g. "Potential Mould"), so match on both.
  const rowHasIssueLabel = (row, label) => {
    if (!Array.isArray(row.issues)) return false;
    const candidates = new Set([label, ...normalizeIssueNames([label])]);
    return row.issues.some((issue) => candidates.has(issue));
  };

  // Read the token & buildingId from localStorage
  const token = localStorage.getItem('managerToken');
  const userStr = localStorage.getItem('managerUser') || "";
  let buildingId = "";
  let managerName = "";
  let managerEmail = "";
  let userObj = null;
  
  if (userStr) {
    try {
      userObj = JSON.parse(userStr);
      buildingId = userObj.buildingId || "";
      managerName = userObj.first_name || "";  
      managerEmail = userObj.email || "";
    } catch (err) {
      console.error("Error parsing user data:", err);
    }
  }

  const localProjectPreview = process.env.NODE_ENV === 'development' &&
    ['localhost', '127.0.0.1'].includes(window.location.hostname) &&
    userObj?.objectId === 'local-project-preview';
  if (localProjectPreview) {
    buildingId = new URLSearchParams(window.location.search).get('localProjectBuilding') || buildingId;
  }

  // Maintenance-role preview support — if a maintenance user opens
  // /?buildingId=<id>&preview=1 (via the View button in MaintenanceHome),
  // use that building instead of whatever's in localStorage. This avoids
  // clobbering the original session in localStorage while still letting
  // the Dashboard render the manager's view for the building.
  try {
    const params = new URLSearchParams(window.location.search);
    const previewId = params.get('buildingId');
    const isPreview = params.get('preview') === '1';
    const roleLc = String(userObj?.role || '').toLowerCase();
    const isMaintenanceRole = roleLc === 'maintenance' || roleLc.startsWith('maintenance-');
    if (previewId && (isPreview || isMaintenanceRole)) {
      buildingId = String(previewId).trim();
    }
  } catch (_) { /* harmless — no URLSearchParams in SSR */ }
  
  const steps = [
    {
      target: 'body',
      content: '👋 Welcome to your Hudson Dashboard! Let\'s take a quick tour to get you started.',
      placement: 'center',
      disableBeacon: true,
    },
    {
      target: '[data-tour="maintenance-filter"]',
      content: '🔍 Use this dropdown to filter maintenance cycles. Select a cycle to view its specific data.',
      disableBeacon: true,
    },
    {
      target: '[data-tour="download-report"]',
      content: '📥 Click here to download the final report PDF for the selected maintenance cycle.',
      disableBeacon: true,
    },
    {
      target: '[data-tour="pills-stats"]',
      content: '📊 Quickly view your building\'s HVAC inspection summary here. See total units, how many have issues, and overall health.',
      disableBeacon: true,
    },
    {
      target: '[data-tour="search-filter"]',
      content: '🔍 Easily find specific units or filter them based on identified HVAC issues for targeted insights.',
      disableBeacon: true,
    },
    {
      target: '[data-tour="table"]',
      content: '📋 This detailed table provides unit statuses, notes, and photos from recent inspections. Sort, filter, or quickly navigate through the results. Click on a "Needs Service" badge to updated a units status.',
      disableBeacon: true,
    },
    {
      target: '[data-tour="riser-chart"]',
      content: '🚿 Monitor the health of your building\'s risers here. Quickly spot problem areas and keep your systems flowing smoothly.',
      disableBeacon: true,
    },
    {
      target: '[data-tour="charts"]',
      content: '📈 Gain deeper insights with interactive charts. Quickly spot trends and common issues to stay ahead.',
      disableBeacon: true,
    },
    {
      target: 'body',
      content: '🎉 That\'s it! Don\'t forget to share the resident portal link in the top right with your residents, so they can easily view their unit-specific HVAC inspection reports and issues.',
      placement: 'center',
      disableBeacon: true,
    },
  ];
  
  

  // Re-show the tour if user clicks help button
  const handleShowTour = () => {
    localStorage.setItem('hasSeenTour', 'false');
    setRunTour(true);
  };

  // Joyride callback
  const handleJoyrideCallback = (data) => {
    const { status } = data;
    if (status === 'finished' || status === 'skipped') {
      localStorage.setItem('hasSeenTour', 'true');
      setRunTour(false);
    }
  };


  function handleOpenNotifyModal(unitRowData) {
    // Ensure dataFormat is included in the row data
    const unitDataWithFormat = {
      ...unitRowData,
      dataFormat: dataFormat // Add dataFormat from state
    };
    setSelectedUnit(unitDataWithFormat); // Pass entire row data, not just unit number
    // setCustomMessage(`Dear Resident of Unit ${unitRowData.unit}, this a reminder that there were identified HVAC issues in your unit.`);

    setCustomMessage(`Dear Resident of Unit ${unitRowData.unit},

This is a notice from your building manager at ${buildingName}. Hudson Condominium Solutions recently conducted a maintenance inspection of your HVAC system and identified some issues that should be addressed to avoid potential damage or worsening over time.

You can view the full inspection report and details of the identified issues here: https://hudsonhvac.ca/unit-report.html?unit=${unitRowData.unit}&a=${buildingId}&add=${encodeURIComponent(buildingName)}`);
      

      
    setShowNotifyModal(true);
  }

  
  // function handleOpenNotifyModal(unitData) {
  //   setSelectedUnit(unitData);
  //   setCustomMessage(`Dear Resident of Unit ${unitData.unit}, this a reminder that there were identified HVAC issues in your unit.`);
  //   setShowNotifyModal(true);
  // }
  
  async function onUpdateStatus(row, newStatus) {
    try {
      // Prepare the payload using objectId rather than unit
      const payload = {
        objectId: row.objectId, // ensure each row has an objectId from Parse
        newStatus: newStatus,
        dataFormat: dataFormat // Pass the data format so server knows which table/format to use
      };
  

      console.log("payload for update", payload)
      // Call your updated route
      const resp = await fetch(`${API_BASE_URL}/api/updateUnitStatus`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', "Authorization": `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        throw new Error(`Server error: ${resp.status}`);
      }
  
      // Refresh data from server to ensure consistency
      // This ensures the status update is reflected correctly and filters work
      const maintenanceId = selectedMaintenance?.maintenanceID || selectedMaintenance?.maintenanceId || selectedCycle;
      if (maintenanceId) {
        // Re-fetch dashboard and table data
        const dashboardResp = await fetch(
          `${API_BASE_URL}/api/getDashboardData`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({
              maintenanceId: maintenanceId,
              dataFormatVersion: dataFormat
            })
          }
        );
        
        const tableResp = await fetch(
          `${API_BASE_URL}/api/getTableData`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({
              maintenanceId: maintenanceId,
              dataFormatVersion: dataFormat,
              limit: 1000,
              skip: 0
            })
          }
        );
        
        if (dashboardResp.ok && tableResp.ok) {
          const dashboardData = await dashboardResp.json();
          const tableData = await tableResp.json();
          
          setStats(dashboardData.stats || { totalUnits: 0, noIssues: 0, foundIssues: 0 });
          // Normalize issue counts to combine drain pan variations
          const normalizedIssueCounts2 = normalizeIssueCounts(dashboardData.issueCounts || {});
          setIssueCounts(normalizedIssueCounts2);
          // Normalize issue names in each row to ensure consistency
          const normalizedUnitsData2 = (tableData.unitsData || []).map(row => {
            if (row.issues && Array.isArray(row.issues)) {
              return { ...row, issues: normalizeIssueNames(row.issues) };
            }
            return row;
          });
          setUnitsData(normalizedUnitsData2);
        }
      } else {
        // Fallback: just update local state if we can't refresh
        setUnitsData((prev) =>
          prev.map((item) =>
            item.objectId === row.objectId
              ? { ...item, inspection: newStatus }
              : item
          )
        );
      }
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Could not update status. Please try again.');
    }
  }
  
  
  function handleCloseNotifyModal() {
    setShowNotifyModal(false);
    setResidentEmail('');
    setSelectedUnit(null);
    setCustomMessage('');
  }
  
  async function handleSendNotification(e) {
    e.preventDefault();
  
    const payload = {
      unit: selectedUnit.unit,
      buildingId: buildingId,
      buildingAddress: buildingName,
      managerName: managerName,
      managerEmail: managerEmail,
      residentEmail: residentEmail,
      residentNotice: customMessage
    };

    // console.log("payload", payload)
  
    try {
      console.log("Sending notification payload:", payload);

      const response = await fetch(`${API_BASE_URL}/api/notify-resident`, {
          method: "POST",
          headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify(payload),
      });
  
      const data = await response.json();
      if (response.ok) {
          handleCloseNotifyModal();

        // Show success alert
        setShowInviteSuccess(true);
        setTimeout(() => setShowInviteSuccess(false), 3000);
      } else {
          alert("Error sending notification: " + data.error);
      }
    } catch (err) {
      console.error("Notification sending error:", err);
      alert("An error occurred while sending notification.");
    }
  }
  
  

  // Fetch building name, cycles, dashboard, table data
  useEffect(() => {
    if (isFetched) return; // only once

    // 1) fetch building name
    const fetchBuildingName = async () => {
      if (!buildingId) return;
      try {
        const resp = await fetch(
          `${API_BASE_URL}/api/getBuildingById`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({ buildingId })
          }
        );
        if (!resp.ok) {
          throw new Error(`Error fetching building name: ${resp.status}`);
        }
        const data = await resp.json();
    
        // Log the entire response from the building fetch
        console.log("Building fetch response:", data);
    
        // The 'name' of the building
        setBuildingName(data.name || "Unknown");
        setBuildingManagerName(Object.prototype.hasOwnProperty.call(data, 'manager') ? data.manager || '' : data.managerProfile?.manager || '');
        setBuildingPhotoUrl(data.photo_url || ""); // Capture photo_url
    
        // Check deviceType to see if it contains "Heat Pump"
        if (Array.isArray(data.deviceType)) {
          const lowerCaseTypes = data.deviceType.map((t) => t.toLowerCase().replace(/\s/g, ''));
          if (lowerCaseTypes.includes("heatpump")) {
            console.log("Setting hvacType to 'heatpump'");
            setHvacType("heatpump");
          } else {
            console.log("Setting hvacType to 'fancoil'");
            setHvacType("fancoil");
          }
        }

        // Check if building has HVAC maintenance contract
        const contracts = data.contracts || [];
        setHasHvacContract(contracts.includes("hvac-maintenance"));
        setHasProjectContract(contracts.includes("project"));
        setHasValveAuditContract(contracts.includes("valve-audit"));
        
      } catch (err) {
        console.error("fetchBuildingName error:", err);
      }
    };
    


    // 2) fetch cycles, pick first
    const fetchCyclesAndData = async () => {
      try {
        console.log("Fetching data from server...");

        let firstMaintenanceId = "";
        let mostRecentCycle = null; // Declare outside the if block so it's accessible later
        let cachedDashData = null;
        
        if (buildingId) {
          // (a) get cycles for building
          const cyclesRes = await fetch(
            `${API_BASE_URL}/api/getMaintenanceInspections`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify({ buildingId })
            }
          );
          if (!cyclesRes.ok) {
            throw new Error(`Maintenance cycles fetch error: ${cyclesRes.status}`);
          }
          const cyclesData = await cyclesRes.json();
          const cycles = cyclesData.maintenanceInspections || [];
          
          // Sort cycles by createdAt in descending order (most recent first)
          const sortedCycles = cycles.sort((a, b) => {
            const dateA = new Date(a.createdAt);
            const dateB = new Date(b.createdAt);
            return dateB - dateA; // Descending order (newest first)
          });

          // Strictly only show in-suite/HVAC cycles on this tab. Project,
          // dryer, kitchen-stack and valve-audit cycles each have their own
          // tab and must never load on the in-suite dashboard (otherwise we
          // get bugs like 16/18 Harbour where the "Actuator Project" cycle
          // was leaking into the in-suite view).
          const hvacCycles = sortedCycles.filter(isInsuiteCycle);

          // Check each in-suite cycle's unit count in parallel to exclude
          // empty (not-yet-started) cycles.
          const unitCountChecks = await Promise.all(
            hvacCycles.map(async (cycle) => {
              try {
                const fv = cycle.dataFormatVersion || 'old';
                const res = await fetch(`${API_BASE_URL}/api/getDashboardData`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                  body: JSON.stringify({ maintenanceId: cycle.maintenanceID, dataFormatVersion: fv }),
                });
                if (!res.ok) return { cycle, totalUnits: 0, dashData: null };
                const dashData = await res.json();
                const total = dashData.stats?.totalUnits || 0;
                return { cycle, totalUnits: total, dashData };
              } catch {
                return { cycle, totalUnits: 0, dashData: null };
              }
            })
          );

          const prioritizedCycles = unitCountChecks
            .filter((r) => r.totalUnits > 0)
            .map((r) => r.cycle);

          setMaintenanceCycles(prioritizedCycles);

          // Default to the most recent cycle with units
          const defaultResult = (localProjectPreview && unitCountChecks.find((r) => r.totalUnits > 0 && isProjectMaintenanceCycle(r.cycle))) ||
            unitCountChecks.find((r) => r.totalUnits > 0);
          if (defaultResult) {
            mostRecentCycle = defaultResult.cycle;
            setSelectedCycle(mostRecentCycle.cycle);
            firstMaintenanceId = mostRecentCycle.maintenanceID;
            setSelectedMaintenance(mostRecentCycle);
            console.log(`[Dashboard] Most recent cycle: ${mostRecentCycle.cycle}, maintenanceID: ${firstMaintenanceId}, dataFormatVersion: ${mostRecentCycle.dataFormatVersion || 'not set'}`);
          } else if (sessionStorage.getItem('justLoggedIn') === '1') {
            // Smart post-login redirect:
            // The user just logged in and landed on the in-suite tab, but this
            // building has no in-suite cycles with data. Find the most recent
            // cycle of another type (dryer/project/kitchen-stack/valve) and
            // bounce them to that tab so they don't see an empty dashboard.
            const fallback = sortedCycles.find((c) => routeForCycle(c));
            if (fallback) {
              const dest = routeForCycle(fallback);
              console.log(`[Dashboard] No in-suite cycles for this building. Redirecting post-login to ${dest} (most recent non-hvac cycle: "${fallback.cycle}", type=${getCycleTypeValue(fallback)})`);
              sessionStorage.removeItem('justLoggedIn');
              navigate(dest, { replace: true });
              return; // bail out — don't waste time loading empty in-suite data
            }
            // No other tab cycles either — clear the flag and let the empty
            // state render.
            sessionStorage.removeItem('justLoggedIn');
          }

          cachedDashData = defaultResult?.dashData || null;
        }

        // (b) if there's a maintenanceId, fetch the dash & table
        if (firstMaintenanceId) {
          // Get format version from the selected cycle
          const formatVersion = mostRecentCycle?.dataFormatVersion || "old";
          console.log(`[Dashboard] Initial load - maintenanceId: ${firstMaintenanceId}, formatVersion: ${formatVersion}`);
          
          // Use cached dashboard data from the unit-count check if available
          const dashData = cachedDashData || await (async () => {
            const dashRes = await fetch(
              `${API_BASE_URL}/api/getDashboardData`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ 
                  maintenanceId: firstMaintenanceId,
                  dataFormatVersion: formatVersion 
                }),
              }
            );
            if (!dashRes.ok) {
              throw new Error(`Dashboard fetch error: ${dashRes.status}`);
            }
            return await dashRes.json();
          })();

          setStats(dashData.stats || {});
          
          // Normalize issue counts to combine drain pan variations
          const normalizedIssueCounts = normalizeIssueCounts(dashData.issueCounts || {});
          console.log('[Dashboard] Raw issueCounts:', dashData.issueCounts);
          console.log('[Dashboard] Normalized issueCounts:', normalizedIssueCounts);
          
          setIssueCounts(normalizedIssueCounts);

          // Override total units for building ID 9823 (16 Yonge St)
          if (buildingId === '9823') {
            const originalStats = dashData.stats || {};
            const foundIssues = originalStats.foundIssues || 0;
            const noIssues = 491 - foundIssues; // Calculate no issues based on 491 total
            
            setStats({
              totalUnits: 491,
              noIssues: Math.max(0, noIssues), // Ensure noIssues doesn't go negative
              foundIssues: foundIssues
            });
          }

          // table
          const pageSize = 1000;
          console.log(`[Dashboard] Fetching table data for maintenanceId: ${firstMaintenanceId}, formatVersion: ${formatVersion}`);
          const tableRes = await fetch(
            `${API_BASE_URL}/api/getTableData`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify({ 
                maintenanceId: firstMaintenanceId, 
                limit: pageSize, 
                skip: 0,
                dataFormatVersion: formatVersion 
              }),
            }
          );
          if (!tableRes.ok) {
            throw new Error(`Table fetch error: ${tableRes.status}`);
          }
          const tableData = await tableRes.json();
          // Normalize issue names in each row to ensure consistency
          const normalizedUnitsData = (tableData.unitsData || []).map(row => {
            if (row.issues && Array.isArray(row.issues)) {
              return { ...row, issues: normalizeIssueNames(row.issues) };
            }
            return row;
          });
          setUnitsData(normalizedUnitsData);
          if (tableData.dataFormat) {
            setDataFormat(tableData.dataFormat);
          }
          console.log(`[Dashboard] Table data received. Format detected: ${tableData.dataFormat || 'unknown'}, Format sent: ${formatVersion}`);
          console.log(`[Dashboard] Units data count: ${tableData.unitsData?.length || 0}`);
        }
      } catch (err) {
        console.error("Error fetching cycles/data:", err);
      } finally {
        // The "just logged in" flag is consumed by this very first load —
        // whether or not we redirected. This prevents a stale flag from
        // triggering an unexpected redirect on a later manual navigation.
        sessionStorage.removeItem('justLoggedIn');
        setIsFetched(true);
        isInitialLoadRef.current = false;
        setIsLoading(false);
      }
    };

    // run them
    fetchBuildingName();
    fetchCyclesAndData();
  }, [token, buildingId, isFetched]);

  // Handle cycle selection change - fetch new data when selectedCycle changes
  const handleCycleChange = async (newCycle) => {
    if (!newCycle || !maintenanceCycles.length) return;

    // Set loading state
    setIsCycleLoading(true);
    setCycleLoadError('');

    try {
      console.log("Cycle changed to:", newCycle);
      
      // Find the selected maintenance cycle
      const selectedMaintenanceCycle = maintenanceCycles.find(cycle => cycle.cycle === newCycle);
      if (!selectedMaintenanceCycle) {
        console.log("No maintenance cycle found for:", newCycle);
        return;
      }

      console.log("Selected maintenance cycle:", selectedMaintenanceCycle);
      const maintenanceId = selectedMaintenanceCycle.maintenanceID;

      // Get format version from the selected cycle
      const formatVersion = selectedMaintenanceCycle?.dataFormatVersion || "old";
      console.log(`[Dashboard] Cycle changed. maintenanceId: ${maintenanceId}, formatVersion: ${formatVersion}`);
      
      // Fetch dashboard data for the selected cycle
      console.log("Fetching dashboard data for maintenanceId:", maintenanceId, "format:", formatVersion);
      const dashRes = await fetch(
        `${API_BASE_URL}/api/getDashboardData`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ 
            maintenanceId,
            dataFormatVersion: formatVersion 
          })
        }
      );

      if (!dashRes.ok) {
        throw new Error(`Dashboard data fetch error: ${dashRes.status}`);
      }

      const dashData = await dashRes.json();
      console.log("Dashboard data received:", dashData);
      // Normalize issue counts to combine drain pan variations
      const normalizedIssueCounts3 = normalizeIssueCounts(dashData.issueCounts || {});
      console.log('[Dashboard] Raw issueCounts (cycle change):', dashData.issueCounts);
      console.log('[Dashboard] Normalized issueCounts (cycle change):', normalizedIssueCounts3);
      

      // Fetch units data for the selected cycle
      console.log("Fetching units data for maintenanceId:", maintenanceId, "format:", formatVersion);
      const pageSize = 1000;
      const unitsRes = await fetch(
        `${API_BASE_URL}/api/getTableData`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ 
            maintenanceId, 
            limit: pageSize, 
            skip: 0,
            dataFormatVersion: formatVersion 
          })
        }
      );

      if (!unitsRes.ok) {
        throw new Error(`Units data fetch error: ${unitsRes.status}`);
      }

      const unitsData = await unitsRes.json();
      // Normalize issue names in each row to ensure consistency
      const normalizedUnitsData3 = (unitsData.unitsData || []).map(row => {
        if (row.issues && Array.isArray(row.issues)) {
          return { ...row, issues: normalizeIssueNames(row.issues) };
        }
        return row;
      });
      // Change the layout and data together, so a failed request cannot label
      // records from the previous cycle as completed project work.
      setSelectedCycle(newCycle);
      setSelectedMaintenance(selectedMaintenanceCycle);
      setProjectStatusFilter('All Statuses');
      setSortColumn('unit');
      setSortDirection('asc');
      setStats(dashData.stats || { totalUnits: 0, noIssues: 0, foundIssues: 0 });
      setIssueCounts(normalizedIssueCounts3);
      setUnitsData(normalizedUnitsData3);
      if (unitsData.dataFormat) {
        setDataFormat(unitsData.dataFormat);
      }
      console.log(`[Dashboard] Units data received. Format detected: ${unitsData.dataFormat || 'unknown'}, Format sent: ${formatVersion}`);
      console.log(`[Dashboard] Units data count: ${unitsData.unitsData?.length || 0}`);

    } catch (err) {
      console.error("Error fetching cycle data:", err);
      setCycleLoadError('Could not load that maintenance cycle. Please try again.');
    } finally {
      setIsCycleLoading(false);
    }
  };

  // Sidebar handlers
  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleNavigate = (key) => {
    setActiveKey(key);
    // Navigate to the appropriate page based on the key
    switch (key) {
      case 'hvac':
        navigate('/');
        break;
      case 'mech':
        navigate('/mechanical-room');
        break;
      case 'dryerVents':
        navigate('/dryer-vents');
        break;
      case 'stackCleaning':
        navigate('/stack-cleaning');
        break;
      case 'waterSavings':
        navigate('/water-savings');
        break;
      case 'reports':
        navigate('/reports');
        break;
    case 'projects':
      navigate('/projects');
      break;
    case 'valveAudit':
      navigate('/valve-audit');
      break;
      default:
        break;
    }
  };

  const EmptyState = () => (
    <div style={{ width: '90%', margin: '2rem auto 0', backgroundColor: '#f8f9fa' }}>
      <Card className="shadow-lg border-0 rounded-lg">
        <Card.Body className="text-center p-5">
          <div style={{ fontSize: '3rem', color: '#224982', marginBottom: '1.5rem' }}>
            <FaSnowflake />
          </div>
          <Card.Title style={{ 
            fontSize: '2rem', 
            marginBottom: '1.5rem', 
            color: '#224982',
            fontWeight: '600'
          }}>
            In-Suite Preventive Maintenance
          </Card.Title>
          <Card.Text style={{ 
            color: '#596272', 
            marginBottom: '2rem',
            fontSize: '1.1rem',
            lineHeight: '1.6',
            maxWidth: '800px',
            margin: '0 auto 2rem'
          }}>
            Ensure the health and efficiency of your building's fan coils, heat pumps, and ERVs with Hudson's in-suite preventive maintenance program. Our solution provides proactive inspections, detailed equipment tracking, and timely resident notifications—helping you prevent costly breakdowns and maintain optimal indoor air quality for every suite.
          </Card.Text>
          <div style={{ fontSize: '1.15rem', color: '#224982', fontWeight: 500 }}>
            Email <a href="mailto:service@hudsonhvac.ca">service@hudsonhvac.ca</a> for a free proposal
          </div>
        </Card.Body>
      </Card>
    </div>
  );

  const LoadingState = () => (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
      <Spinner animation="border" role="status" style={{ color: '#224982' }}>
        <span className="visually-hidden">Loading...</span>
      </Spinner>
    </div>
  );

  // If loading => show spinner
  if (isLoading) {
    return (
      <div style={{ display: 'flex' }}>
        <div
          className="dashboard-sidebar-host"
          onMouseEnter={() => setSidebarCollapsed(false)}
          onMouseLeave={() => setSidebarCollapsed(true)}
          style={{ height: '100vh' }}
        >
        <Sidebar
          managerName={buildingManagerName}
          activeKey={activeKey}
          onNavigate={handleNavigate}
          collapsed={sidebarCollapsed}
          onToggle={handleSidebarToggle}
          buildingName={buildingName}
          buildingPhotoUrl={buildingPhotoUrl}
          showProjectsTab={hasProjectContract}
          showValveAuditTab={hasValveAuditContract}
        />
        </div>
        
        <div className="dashboard-content manager-dashboard" style={{
          flex: 1,
          minWidth: 0,
          marginLeft: sidebarCollapsed ? '72px' : '240px',
          transition: 'margin-left 240ms',
        }}>
          <DashboardNavbar
            logoutInSidebar
            onShowTour={handleShowTour}
            selectedMaintenance={selectedMaintenance}
            buildingName={buildingName}
            buildingId={buildingId}
            contractLabel="In-Suite Preventive Maintenance"
          />
          <Header
            maintenanceCycles={maintenanceCycles}
            selectedCycle={selectedCycle}
            setSelectedCycle={setSelectedCycle}
            selectedMaintenance={selectedMaintenance}
            contractLabel="In-Suite Preventive Maintenance"
            reportFieldName="finalReport"
          />
          <LoadingState />
        </div>
      </div>
    );
  }

  // If no HVAC contract, show empty state
  if (!hasHvacContract && !projectMode) {
    return (
      <div style={{ display: 'flex' }}>
        <div
          className="dashboard-sidebar-host"
          onMouseEnter={() => setSidebarCollapsed(false)}
          onMouseLeave={() => setSidebarCollapsed(true)}
          style={{ height: '100vh' }}
        >
          <Sidebar
            managerName={buildingManagerName}
            activeKey={activeKey}
            onNavigate={handleNavigate}
            collapsed={sidebarCollapsed}
            onToggle={handleSidebarToggle}
            buildingName={buildingName}
            buildingPhotoUrl={buildingPhotoUrl}
            showProjectsTab={hasProjectContract}
            showValveAuditTab={hasValveAuditContract}
          />
        </div>
        
        <div className="dashboard-content manager-dashboard" style={{
          flex: 1,
          minWidth: 0,
          marginLeft: sidebarCollapsed ? '72px' : '240px',
          transition: 'margin-left 240ms',
        }}>
          <DashboardNavbar
            logoutInSidebar
            onShowTour={handleShowTour}
            selectedMaintenance={selectedMaintenance}
            buildingName={buildingName}
            buildingId={buildingId}
            contractLabel="In-Suite Preventive Maintenance"
          />
          <Header
            maintenanceCycles={maintenanceCycles}
            selectedCycle={selectedCycle}
            setSelectedCycle={setSelectedCycle}
            selectedMaintenance={selectedMaintenance}
            contractLabel="In-Suite Preventive Maintenance"
            reportFieldName="finalReport"
          />
          <EmptyState />
        </div>
      </div>
    );
  }

  // ========== Filter Logic ==========
  
  // Helper function to check if a row has custom/other issues
  const rowHasCustomIssue = (row) => {
    // Check for custom issues in rawData
    const hasCustomIssueInRawData = row.rawData && (
      row.rawData.any_other_custom_issues === "HAS ISSUES 🔴" ||
      row.rawData.anyOtherCustomIssues === "HAS ISSUES 🔴" ||
      (Array.isArray(row.rawData.any_other_custom_issues) && row.rawData.any_other_custom_issues.includes("HAS ISSUES 🔴")) ||
      (Array.isArray(row.rawData.anyOtherCustomIssues) && row.rawData.anyOtherCustomIssues.includes("HAS ISSUES 🔴")) ||
      (row.rawData.photo_of_custom_issue && row.rawData.photo_of_custom_issue.length > 0) ||
      (row.rawData.anyOtherCustomIssuesPhoto && row.rawData.anyOtherCustomIssuesPhoto.length > 0) ||
      (row.rawData.description_of_custom_issue && row.rawData.description_of_custom_issue.trim() !== '') ||
      (row.rawData.anyOtherCustomIssuesDescription && row.rawData.anyOtherCustomIssuesDescription.trim() !== '')
    );
    
    // Fallback: Needs Service with no issues means custom/other issue
    const needsServiceWithoutIssues = row.inspection === "Needs Service" && 
      (!row.issues || row.issues.length === 0) && !hasCustomIssueInRawData;
    
    return hasCustomIssueInRawData || needsServiceWithoutIssues;
  };
  
  const filteredData = unitsData.filter((row) => {
    if (projectMode) {
      const matchesStatus = projectStatusFilter === 'All Statuses' || getProjectStatus(row) === projectStatusFilter;
      const search = searchTerm.trim().toLowerCase();
      return matchesStatus && [row.unit, row.equipmentLocation, getProjectNotes(row)]
        .some(value => String(value || '').toLowerCase().includes(search));
    }
    // Status filter (array). "All Status" disables filtering entirely.
    const activeStatuses = Array.isArray(statusFilter) ? statusFilter : [statusFilter];
    if (activeStatuses.length > 0 && !activeStatuses.includes('All Status')) {
      if (!activeStatuses.includes(row.inspection)) {
        return false;
      }
    }

    // Search match
    const unitStr = row.unit?.toLowerCase() || '';
    const notesStr = row.notes?.toLowerCase() || '';
    const st = searchTerm.toLowerCase();
    const searchMatch = unitStr.includes(st) || notesStr.includes(st);

    // Issue filters
    if (issueFilters.length > 0) {
      let hasAnyIssue = false;
      for (let label of issueFilters) {
        // Special handling for "Other" filter - check custom issues
        if (label === "Other") {
          if (row.issues && row.issues.includes("Other")) {
            hasAnyIssue = true;
            break;
          }
          // Also check for custom issues in rawData
          if (rowHasCustomIssue(row)) {
            hasAnyIssue = true;
            break;
          }
        }
        // For new format, check the issues array
        else if (dataFormat === "new" && row.issues) {
          if (rowHasIssueLabel(row, label)) {
            hasAnyIssue = true;
            break;
          }
        } else {
          // For old format, check boolean columns
          const realKey = columnMap[label];
          if (realKey && row[realKey] === true) {
            hasAnyIssue = true;
            break;
          }
        }
      }
      if (!hasAnyIssue) return false;
    }
    return searchMatch;
  });

  // ========== Sort ==========
  const sortedData = [...filteredData].sort((a, b) => {
    const projectSortValue = row => sortColumn === 'inspection' ? getProjectStatus(row)
      : sortColumn === 'notes' ? getProjectNotes(row) : row[sortColumn];
    let valA = projectMode ? projectSortValue(a) : a[sortColumn];
    let valB = projectMode ? projectSortValue(b) : b[sortColumn];

    // If sorting by "unit", parse them as numbers
    if (sortColumn === 'unit') {
      valA = parseInt(valA, 10);
      valB = parseInt(valB, 10);
    }
    if (typeof valA === 'string') valA = valA.toLowerCase();
    if (typeof valB === 'string') valB = valB.toLowerCase();

    if (valA < valB) return (sortDirection === 'asc' ? -1 : 1);
    if (valA > valB) return (sortDirection === 'asc' ? 1 : -1);
    return 0;
  });

  // ========== Paginate ==========
  const totalPages = Math.max(1, Math.ceil(sortedData.length / rowsPerPage));
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const pageData = sortedData.slice(startIndex, endIndex);

  // ========== Handlers ==========
  function handleShowProjectNoAccess() {
    setSearchTerm('');
    setProjectStatusFilter('No Access');
    setCurrentPage(1);
  }
  function handleRowsPerPageChange(e) {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  }
  function handlePrevPage() {
    if (currentPage > 1) setCurrentPage(prev => prev - 1);
  }
  function handleNextPage() {
    if (currentPage < totalPages) setCurrentPage(prev => prev + 1);
  }
  function handleSort(columnKey) {
    if (sortColumn === columnKey) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  }

  // Slideshow
  function handleOpenCarousel(photos, index) {
    setCarouselPhotos(photos);
    setCarouselIndex(index);
    setShowModal(true);
  }
  function handleCloseModal() {
    setShowModal(false);
  }
  function handleSelectCarousel(selectedIndex) {
    setCarouselIndex(selectedIndex);
  }

  const fancoilHealth = (stats.totalUnits && stats.totalUnits > 0)
  ? Math.round((stats.noIssues / stats.totalUnits) * 100) + '%'
  : '-';
  
  return (
    <div style={{ display: 'flex' }}>
      <div
        className="dashboard-sidebar-host"
        onMouseEnter={() => setSidebarCollapsed(false)}
        onMouseLeave={() => setSidebarCollapsed(true)}
        style={{ height: '100vh' }}
      >
        <Sidebar
          managerName={buildingManagerName}
          activeKey={activeKey}
          onNavigate={handleNavigate}
          collapsed={sidebarCollapsed}
          onToggle={handleSidebarToggle}
          buildingName={buildingName}
          buildingPhotoUrl={buildingPhotoUrl}
          showProjectsTab={hasProjectContract}
          showValveAuditTab={hasValveAuditContract}
        />
      </div>
      <div
        className="dashboard-content manager-dashboard"
        style={{
          flex: 1,
          minWidth: 0,
          marginLeft: sidebarCollapsed ? '72px' : '240px',
          transition: 'margin-left 240ms',
        }}
      >
        {showInviteSuccess && (
          <Alert
            variant="success"
            style={{
              position: 'fixed',
              top: '10px',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 9999,
              margin: 0,
              padding: '0.5rem 1rem',
            }}
          >
            <strong>Notification sent successfully!</strong>
          </Alert>
        )}
        <Joyride
          steps={steps}
          run={runTour && !projectMode}
          continuous
          showSkipButton
          disableBeacon
          callback={handleJoyrideCallback}
          locale={{
            back: 'Back',
            close: 'Close',
            last: 'Done',
            next: 'Next',
            skip: 'Skip',
          }}
          styles={{
            options: {
              zIndex: 10000,
              primaryColor: '#224982',
            },
          }}
        />

        {/* Navbar + Header */}
        <div data-tour="resident-portal">
          <DashboardNavbar
            logoutInSidebar
            onShowTour={handleShowTour}
            selectedMaintenance={selectedMaintenance}
            buildingName={buildingName}
            buildingId={buildingId}
            contractLabel={contractLabel}
          />
        </div>

        <Header
          maintenanceCycles={maintenanceCycles}
          selectedCycle={selectedCycle}
          setSelectedCycle={handleCycleChange}
          selectedMaintenance={selectedMaintenance}
          contractLabel={contractLabel}
          reportFieldName="finalReport"
        />

        {/* Cycle Loading Overlay */}
        {cycleLoadError && <Alert variant="danger" style={{ width: '90%', margin: '1rem auto' }}>{cycleLoadError}</Alert>}
        {isCycleLoading && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}>
            <Spinner animation="border" role="status" style={{ color: '#2c59a2', width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        )}

        {/* Warning Alert for Low Health */}
        {(() => {
          if (projectMode) return null;
          if (stats.totalUnits === 0) return null;
          const healthPercentage = Math.round((stats.noIssues / stats.totalUnits) * 100);
          if (healthPercentage < 41) {
            return (
              <div style={{ width: '90%', margin: '1rem auto' }}>
                <div
                  style={{
                    backgroundColor: '#f8d7da',
                    border: '1px solid #f5c6cb',
                    borderRadius: '8px',
                    padding: '0.75rem 1.25rem',
                    color: '#721c24',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    boxShadow: '0 2px 4px rgba(220, 53, 69, 0.1)',
                  }}
                >
                  <div style={{ fontSize: '1.25rem', color: '#dc3545' }}>
                    ⚠️
                  </div>
                  <div style={{ fontSize: '0.95rem', lineHeight: '1.4' }}>
                    <strong>HVAC Health Alert:</strong> More than {100 - healthPercentage}% of fancoil units in the building had identified issues. This building may potentially have elevated risk of water damage, potential mould growth, and equipment problems. An engineer assessment report is recommended.
                  </div>
                </div>
              </div>
            );
          }
          return null;
        })()}

        {/* Resident notification guide banner — shows whenever there are
            units to follow up on, even when the Critical HVAC Health Alert
            above is also showing. Managers want to see both: the alert
            explains the severity, the instructions tell them what to do. */}
        {!projectMode && stats.foundIssues > 0 && (
          <div className="sdg-banner">
            <div className="sdg-banner-icon">
              <FaEnvelopeOpenText />
            </div>
            <div className="sdg-banner-content">
              <div className="sdg-banner-title">
                <span className="sdg-banner-headline">
                  Send Required Deficiency Notice to Residents
                </span>
                <span className="sdg-banner-dot" aria-hidden="true">•</span>
                <span className="sdg-banner-tag">Action needed</span>
              </div>
              <div className="sdg-banner-subtitle">
                <strong>{stats.foundIssues}</strong>{' '}
                {stats.foundIssues === 1 ? 'unit must' : 'units must'} be notified for
                necessary repairs. See how to send all notices in under a minute.
              </div>
            </div>
            <Button
              className="sdg-banner-btn"
              onClick={() => setShowShareGuide(true)}
            >
              View Instructions →
            </Button>
          </div>
        )}

        {/* Stats (pills) */}
        <LocalProjectPreviewPicker enabled={localProjectPreview} buildingId={buildingId} />
        {projectMode ? <ProjectSummary rows={unitsData} onShowNoAccess={handleShowProjectNoAccess} /> : <Pills
          stats={stats}
          hvacType={hvacType}
          unitsData={unitsData}
          cycleName={selectedMaintenance?.cycle || selectedCycle || ''}
          onShowMissed={() => setStatusFilter(['Missed'])}
        />}

        {/* SEARCH + FILTERS */}
        {projectMode ? <div className="project-filters">
          <div className="project-filters-search">
            <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} placeholder="Search unit or location" />
          </div>
          <div className="project-filters-status">
            <StatusMultiSelect
              ariaLabel="Filter project status"
              allStatusValue="All Statuses"
              options={['All Statuses', 'Completed', 'No Access', ...(unitsData.some(row => getProjectStatus(row) === 'Pending') ? ['Pending'] : [])].map(value => ({ value, label: value }))}
              statusFilter={projectStatusFilter}
              setStatusFilter={setProjectStatusFilter}
            />
          </div>
        </div> : <div
          className="manager-data-filters"
          style={{
            width: '90%',
            margin: '1rem auto',
            display: 'flex',
            gap: '1rem',
            flexWrap: 'nowrap',
          }}
        >
          <div style={{ flex: 4 }}>
            <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
          </div>
          <div style={{ flex: 2 }}>
            <IssueMultiSelect
              issueFilters={issueFilters}
              setIssueFilters={setIssueFilters}
            />
          </div>
          <div style={{ flex: 2 }}>
            <StatusMultiSelect
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
            />
          </div>
        </div>}

        {/* Table */}
        <div data-tour="table">
          <FancoilTable
            projectMode={projectMode}
            hvacType={hvacType}
            pageData={pageData}
            sortedData={sortedData}
            startIndex={startIndex}
            endIndex={endIndex}
            currentPage={currentPage}
            totalPages={totalPages}
            rowsPerPage={rowsPerPage}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            handleSort={handleSort}
            handlePrevPage={handlePrevPage}
            handleNextPage={handleNextPage}
            handleRowsPerPageChange={handleRowsPerPageChange}
            showModal={showModal}
            carouselPhotos={carouselPhotos}
            carouselIndex={carouselIndex}
            handleOpenCarousel={handleOpenCarousel}
            handleCloseModal={handleCloseModal}
            handleSelectCarousel={handleSelectCarousel}
            isVideoUrl={isVideoUrl}
            buildingId={buildingId}
            onNotifyResident={handleOpenNotifyModal}         
            onUpdateStatus={onUpdateStatus}
            dataFormat={dataFormat}
          />
        </div>

        {/* Riser Health */}
        {!projectMode && <><div data-tour="riser-chart" style={{ width: '90%', margin: '2rem auto' }}>
        <RiserHealthChart unitsData={unitsData} hvacType={hvacType} dataFormat={dataFormat} />
        </div>

        {/* Pie Chart + Building Insights */}
        <div
          className="manager-analytics-grid"
          data-tour="charts"
          style={{
            width: '90%',
            margin: '2rem auto',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '1rem',
            alignItems: 'stretch',
          }}
        >
          {/* Pie Chart */}
          <div
            style={{
              flex: '1 1 400px',
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: '#fff',
              borderRadius: '8px',
              boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
              padding: '1rem',
              minWidth: '300px',
              marginBottom: '1rem',
            }}
          >
          <h5 style={{ marginBottom: '1rem', fontWeight: '600' }}>
            {hvacType === 'heatpump' 
              ? 'Heatpumps Health Breakdown' 
              : 'Fancoils Health Breakdown'}
          </h5>
            <div
              style={{
                flex: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <PieChart noIssues={stats.noIssues} foundIssues={stats.foundIssues} />
              </div>
            </div>
          </div>

          {/* Building Insights */}
          <div
            style={{
              flex: '1 1 400px',
              display: 'flex',
              flexDirection: 'column',
              minWidth: '300px',
              marginBottom: '1rem',
            }}
          >
            <BuildingInsights unitsData={unitsData} issueCounts={issueCounts} />
          </div>
        </div>

        </>}
        <Footer />

        <NotifyResidentModal
          show={showNotifyModal}
          handleClose={handleCloseNotifyModal}
          handleSend={handleSendNotification}
          residentEmail={residentEmail}
          setResidentEmail={setResidentEmail}
          customMessage={customMessage}
          unitData={selectedUnit}
          buildingId={buildingId}
          buildingName={buildingName}
          setCustomMessage={setCustomMessage}
          hvacType={hvacType}
        />

        <ShareDeficienciesGuide
          show={showShareGuide}
          onClose={() => setShowShareGuide(false)}
          unitsData={unitsData}
          buildingName={buildingName}
          buildingId={buildingId}
          hvacType={hvacType}
        />
      </div>
    </div>
  );
}

export default Dashboard;
